import React, { useRef, useState } from 'react';
import { importSpecies } from '../../services/speciesService';
import { parseSpeciesImport, SPECIES_IMPORT_TEMPLATES } from '../../utils/speciesImport';

export default function SpeciesImport({ onImported }) {
  const [records, setRecords] = useState([]);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [busy, setBusy] = useState(false);
  const [confirmed, setConfirmed] = useState(false);
  const generation = useRef(0);
  const load = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    const current = ++generation.current;
    setRecords([]); setError(''); setSuccess(''); setConfirmed(false);
    if (!file) return;
    try {
      if (file.size > 1024 * 1024) throw new Error('Species files must be at most 1 MB.');
      const parsed = parseSpeciesImport(await file.text());
      if (current === generation.current) setRecords(parsed);
    } catch (err) { if (current === generation.current) setError(err.message); }
  };
  const save = async () => {
    if (busy || !confirmed || !records.length) return;
    setBusy(true); setError(''); setSuccess('');
    try {
      const response = await importSpecies(records);
      setSuccess(`Added ${response.data.created} species. Left ${response.data.skipped} existing species unchanged.`);
      setRecords([]); setConfirmed(false);
      onImported?.();
    } catch (err) { setError(err.response?.data?.message || 'Import failed. No success was confirmed; retry safely.'); }
    finally { setBusy(false); }
  };
  return <details style={{ padding: 12, border: '1px solid #cbd5e1', borderRadius: 8, marginBottom: 16 }}>
    <summary>Import shared species (CSV / JSON)</summary>
    <p>This shared catalogue supplies both guided harvest logging and stewardship species selection. Imports add species, not lake assignments or published guidance. Existing species are never overwritten.</p>
    <p>Required: commonName and category. Optional: slug, scientificName, communityName, harvestCategory, alternateNames, subType, shortDescription, habitat, seasonalNotes, stewardshipNote.</p>
    <p>Categories: Fish, Mammal, Bird, Reptile, Amphibian, Tree, Plant, Berry, Fungus, Medicine, Aquatic Resource, General Resource, Environmental Observation. Harvest categories: fishing, hunting, birds, plants_medicine, environmental, other.</p>
    <p>CSV uses a header row and | between alternate names. JSON accepts an array of objects or an object with a species array; alternateNames is an array. Maximum 500 species / 1 MB.</p>
    <p>Download a template with all supported fields. Replace the example species with your own rows;
      leave unused optional fields blank. Downloading does not import anything.</p>
    <div style={{ display: 'flex', flexWrap: 'wrap', gap: 16, marginBottom: 12 }}>
      <a download="cip-species-template.csv" href={`data:text/csv;charset=utf-8,${encodeURIComponent(SPECIES_IMPORT_TEMPLATES.csv)}`}>Download CSV template</a>
      <a download="cip-species-template.json" href={`data:application/json;charset=utf-8,${encodeURIComponent(SPECIES_IMPORT_TEMPLATES.json)}`}>Download JSON template</a>
    </div>
    <pre style={{ overflowX: 'auto' }}>{'commonName,category,harvestCategory,alternateNames\nWalleye,Fish,fishing,Pickerel|Dore\nMoose,Mammal,hunting,'}</pre>
    <pre style={{ overflowX: 'auto' }}>{'[{"commonName":"Walleye","category":"Fish","harvestCategory":"fishing","alternateNames":["Pickerel"]}]'}</pre>
    <label>Species CSV or JSON file <input type="file" accept=".csv,.json" disabled={busy} onChange={load} /></label>
    {error && <p role="alert">{error}</p>}
    {success && <p role="status">{success}</p>}
    {!!records.length && <>
      <p>{records.length} species ready for validation and import:</p>
      <ul style={{ maxHeight: 180, overflowY: 'auto' }}>{records.map((record, index) => <li key={index}>{record.commonName} — {record.category}{record.harvestCategory ? ` (${record.harvestCategory})` : ''}</li>)}</ul>
      <label><input type="checkbox" checked={confirmed} disabled={busy} onChange={(event) => setConfirmed(event.target.checked)} /> Add these species to the shared catalogue for all communities. Existing slugs are skipped, not updated.</label>
      <button type="button" disabled={busy || !confirmed} onClick={save}>{busy ? 'Importing...' : 'Import species'}</button>
    </>}
  </details>;
}
